import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useRouting } from 'hooks/useRouting';
import { getPortfolioAnalysis } from 'config/api';
import {
  ManagementType,
  ManagementItem,
  PortfolioAnalysisItem,
  TargetId,
  PortfolioAssetType,
  PortfolioAnalysis,
} from '../utils/type';
import {
  getManagementItemInfo,
  portfolioTypeToBackgroundColor,
  portfolioTypeToString,
} from '../utils/portfolioInfo';
import { useBirthday } from '../utils/birthday';
import { PortfolioChart } from '../components/features/portfolio-analysis/portfolio-chart';
import { PortfolioAnalysisLoading } from '../components/features/portfolio-analysis/share/PortfolioAnalysisLoading';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';

const PortfolioAnalysisChangeManagementCompleteInternal = ({
  selectedManagementType,
  portfolioAnalysis,
}: {
  selectedManagementType: ManagementType;
  portfolioAnalysis: PortfolioAnalysis;
}): React.ReactElement => {
  const routing = useRouting();
  const goPortfolioAnalysis = (): void => {
    routing.backRouting(true, 2);
  };

  const back = () => {
    routing.backRouting(true, 2);
  };
  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    setBack({ backFunction: back });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedManagementItemInfo = getManagementItemInfo(
    selectedManagementType
  );

  const managementItems: ManagementItem[] = portfolioAnalysis.managementItems;
  const portfolio = managementItems.find(
    managementItems => selectedManagementType === managementItems.managementType
  );

  if (selectedManagementItemInfo === undefined || portfolio === undefined)
    return <></>;

  const dispSelectedPortfolios = selectedManagementItemInfo.dispType
    .map(dispType => {
      if (dispType === 'OTHER') return undefined;

      return portfolio.itemsByPortfolioAnalysis.find(
        item => item.assetType === dispType
      );
    })
    .filter(e => e) as PortfolioAnalysisItem[] | undefined;

  if (dispSelectedPortfolios === undefined) return <></>;

  return (
    <div className="p-4 bg-white">
      <div className="pt-4 px-8 text-sm font-bold text-center">
        <span className={`${selectedManagementItemInfo.titleColor}`}>
          {selectedManagementItemInfo.title}
        </span>
        {selectedManagementItemInfo.subTitle}
        に
        <br />
        変更が完了しました。
      </div>
      <div className="flex flex-row justify-center">
        <div className="flex items-center mx-2">
          <img
            style={{ width: '80px', height: '80px' }}
            src={`/static/images/advisor/asset_icon/${selectedManagementItemInfo.imgName}.svg`}
            alt="アイコン"
          />
        </div>
        <div className="mx-2">
          <PortfolioChart
            {...{
              chart: {
                title: '',
              },
              data: dispSelectedPortfolios.map(p => {
                return {
                  id: p.assetType,
                  value: p.targetPer,
                };
              }),
              idToColor: portfolioTypeToBackgroundColor,
              idToTitle: portfolioTypeToString,
              valueFormatter: value =>
                commaFormat(value, { numberOfDecimals: 0 }),
            }}
          />
        </div>
      </div>
      <div className="flex-col flex items-center">
        <div className="flex flex-col p-4 gap-2" style={{ minHeight: '155px' }}>
          {dispSelectedPortfolios.map((p, key) => {
            return (
              <div className="flex flex-row items-center" key={key}>
                <div
                  className="rounded-full w-3 h-3"
                  style={{
                    backgroundColor: portfolioTypeToBackgroundColor(
                      p.assetType
                    ),
                  }}></div>
                <div className="ml-2 mr-6 text-xs whitespace-no-wrap">
                  {portfolioTypeToString(p.assetType)}
                </div>
                <div className="ml-auto text-xs">
                  {commaFormat(p.targetPer, {
                    numberOfDecimals: 1,
                  })}
                  %
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col py-4 mx-2">
        <button
          className={`text-bluegray-500 font-bold text-sm p-2`}
          onClick={goPortfolioAnalysis}>
          ポートフォリオ分析TOPに戻る
        </button>
      </div>
    </div>
  );
};

const PortfolioAnalysisChangeManagementComplete = (): React.ReactElement => {
  const { id, type } = useParams<{ id: string; type: string }>();
  const selectedManagementType = Number(type) as ManagementType;

  useCustomHeaderTitle('運用タイプ変更画面');

  const { birthday } = useBirthday();

  const { data: portfolioAnalysis, isFetching } = useQuery(
    'portfolioAnalysisChangeManagementPortfolioAnalysis',
    () => getPortfolioAnalysisAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (
    portfolioAnalysis === null ||
    portfolioAnalysis === undefined ||
    isFetching
  )
    return <PortfolioAnalysisLoading />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <PortfolioAnalysisChangeManagementCompleteInternal
        {...{ portfolioAnalysis, selectedManagementType }}
      />
    </PremiumCheck>
  );
};

const getPortfolioAnalysisAPI = async (id: TargetId) => {
  const res_ = await getPortfolioAnalysis(id);
  const res = {
    itemsByPortfolioAnalysis: res_.ItemsByPortfolioAnalysis.map(item => {
      return {
        assetType: item.AssetType as PortfolioAssetType,
        currentPer: item.CurrentPer,
        targetPer: item.TargetPer,
      };
    }),
    registeredAccountName: res_.RegisteredAccountName,
    targetReturnPer: res_.TargetReturnPer,
    targetReturnLimitPer: res_.TargetReturnLimitPer,
    updateDateOfAsset: res_.UpdateDateOfAsset,
    managementType: res_.ManagementType as ManagementType,
    managementItems: res_.ManagementItems.map(managementItem => {
      return {
        managementType: managementItem.ManagementType as ManagementType,
        itemsByPortfolioAnalysis: managementItem.ItemsByPortfolioAnalysis.map(
          item => {
            return {
              assetType: item.AssetType as PortfolioAssetType,
              currentPer: item.CurrentPer,
              targetPer: item.TargetPer,
            };
          }
        ),
      };
    }),
  };

  return res as PortfolioAnalysis;
};

export default PortfolioAnalysisChangeManagementComplete;
